import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User, MapPin, Loader2, ArrowRight, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

const ROLES = [
  { id: "Buyer", desc: "Source & purchase commodities" },
  { id: "Seller", desc: "List & sell your commodities" },
  { id: "Broker", desc: "Connect buyers and sellers" },
];

export default function Auth() {
  const nav = useNavigate();
  const { login, register, googleLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", location: "", bio: "" });
  const [roles, setRoles] = useState(["Buyer"]);
  const [resetToken, setResetToken] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [terms, setTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    // Emergent Google OAuth callback: session_id arrives in the URL fragment
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        (async () => {
          try {
            await googleLogin(sid);
            window.history.replaceState(null, "", window.location.pathname);
            nav("/");
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
          }
        })();
      }
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset")) setResetToken(params.get("reset"));
  }, [googleLogin, nav]);

  const googleSignIn = () => {
    if (mode === "signup" && !terms) return toast.error("Please accept the Terms & Conditions first");
    if (mode === "signup") localStorage.setItem("goe-terms-accepted", "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = async () => {
    if (!form.email.trim()) return toast.error("Enter your email above first");
    try {
      await api.post("/auth/forgot-password", { email: form.email.trim() });
      toast.success("If that email exists, a reset link has been sent.");
    } catch { toast.error("Could not send reset link"); }
  };

  const doReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: resetPw });
      toast.success("Password updated. Please log in.");
      window.history.replaceState(null, "", "/auth");
      setResetToken(null); setMode("login");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleRole = (r) =>
    setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email.trim(), form.password);
      } else {
        if (roles.length === 0) throw { response: { data: { detail: "Select at least one role" } } };
        if (!terms) throw { response: { data: { detail: "You must accept the Terms & Conditions to create an account" } } };
        await register({ ...form, email: form.email.trim(), roles, accepted_terms: true });
      }
      try { navigator.vibrate?.(20); } catch {}
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell relative flex min-h-screen flex-col justify-center overflow-hidden px-6 py-10">
      <div className="pointer-events-none absolute -right-20 -top-16 h-72 w-72 rounded-full blur-3xl"
           style={{ background: "radial-gradient(circle,rgba(245,158,11,0.45),transparent 70%)" }} />
      <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 22 }}>
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="floaty mb-3 rounded-3xl px-7 py-5"
               style={{ background: "#0B0C10", border: "1px solid rgba(245,158,11,0.22)", boxShadow: "0 0 34px rgba(245,158,11,0.16)" }}>
            <img src="/logo.jpg" alt="Global Ore Exchange" className="w-52 max-w-[62vw]" style={{ mixBlendMode: "screen" }} />
          </div>
          <p className="mt-2 text-sm text-dim">The trusted commodity marketplace</p>
        </div>

        {resetToken && (
          <div className="glass mb-5 rounded-2xl p-4" data-testid="reset-panel">
            <p className="mb-2 font-bold" style={{ color: "var(--text)" }}>Set a new password</p>
            <div className="glass mb-3 flex items-center gap-3 rounded-2xl px-4 py-3">
              <Lock size={18} className="text-dim" />
              <input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password"
                     data-testid="reset-password" className="w-full bg-transparent outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
            </div>
            <button onClick={doReset} data-testid="reset-submit" className="btn-accent w-full py-3 font-bold">Update Password</button>
          </div>
        )}

        <div className="glass mb-5 flex rounded-2xl p-1">
          {["login", "signup"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
                    className="pressable relative flex-1 rounded-xl py-2.5 text-sm font-bold"
                    style={{ color: mode === m ? "#fff" : "var(--text-dim)" }}>
              {mode === m && (
                <motion.div layoutId="authtab" className="absolute inset-0 rounded-xl"
                            style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }} />
              )}
              <span className="relative">{m === "login" ? "Log In" : "Sign Up"}</span>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <AnimatePresence mode="popLayout">
            {mode === "signup" && (
              <motion.div key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <Field icon={User} placeholder="Full name" value={form.name} onChange={set("name")} testid="auth-name" required />
              </motion.div>
            )}
          </AnimatePresence>
          <Field icon={Mail} type="email" placeholder="Email address" value={form.email} onChange={set("email")} testid="auth-email" required />
          <Field icon={Lock} type="password" placeholder="Password" value={form.password} onChange={set("password")} testid="auth-password" required />

          <AnimatePresence mode="popLayout">
            {mode === "signup" && (
              <motion.div key="extra" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <Field icon={MapPin} placeholder="Location (city, country)" value={form.location} onChange={set("location")} testid="auth-location" />
                <div>
                  <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-dim">I am a…</p>
                  <div className="space-y-2">
                    {ROLES.map((r) => {
                      const active = roles.includes(r.id);
                      return (
                        <button type="button" key={r.id} onClick={() => toggleRole(r.id)}
                                data-testid={`role-${r.id}`}
                                className="pressable flex w-full items-center justify-between rounded-2xl border p-3 text-left"
                                style={{ borderColor: active ? "var(--accent)" : "var(--border)",
                                         background: active ? "rgba(245,158,11,0.10)" : "var(--surface)" }}>
                          <div>
                            <p className="font-bold" style={{ color: active ? "var(--accent)" : "var(--text)" }}>{r.id}</p>
                            <p className="text-xs text-dim">{r.desc}</p>
                          </div>
                          <div className="flex h-5 w-5 items-center justify-center rounded-full border-2"
                               style={{ borderColor: active ? "var(--accent)" : "var(--border)", background: active ? "var(--accent)" : "transparent" }}>
                            {active && <div className="h-2 w-2 rounded-full bg-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <TermsBox checked={terms} onToggle={() => setTerms((t) => !t)} open={showTerms} onOpen={() => setShowTerms((s) => !s)} />
              </motion.div>
            )}
          </AnimatePresence>

          <button type="submit" disabled={loading || (mode === "signup" && !terms)} data-testid="auth-submit"
                  className="btn-accent flex w-full items-center justify-center gap-2 py-3.5 text-base font-bold disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin" size={20} /> : (
              <>{mode === "login" ? "Log In" : "Create Account"} <ArrowRight size={18} /></>
            )}
          </button>

          {mode === "login" && (
            <button type="button" onClick={forgot} data-testid="forgot-password"
                    className="pressable mx-auto block text-sm font-semibold text-dim">Forgot password?</button>
          )}

          <div className="flex items-center gap-3 py-1">
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
            <span className="text-xs text-dim">or</span>
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>

          <button type="button" onClick={googleSignIn} data-testid="google-signin"
                  className="pressable glass flex w-full items-center justify-center gap-3 rounded-2xl py-3.5 font-bold" style={{ color: "var(--text)" }}>
            <svg width="19" height="19" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.7 34.5 27 35.5 24 35.5c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.6 5.6C40.9 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-dim">Email & password authentication · secure & private</p>
      </motion.div>
    </div>
  );
}

function TermsBox({ checked, onToggle, open, onOpen }) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: checked ? "var(--accent)" : "var(--border)", background: "var(--surface)" }}
         data-testid="terms-box">
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" checked={checked} onChange={onToggle} data-testid="terms-checkbox"
               className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-amber-500" />
        <span className="text-xs leading-relaxed text-dim">
          I have read and agree to the{" "}
          <button type="button" onClick={onOpen} data-testid="terms-toggle" className="font-bold underline accent">Terms & Conditions</button>,
          including the liability disclaimer below.
        </span>
      </label>
      {open && (
        <div className="no-scrollbar mt-3 max-h-40 space-y-2 overflow-y-auto rounded-xl p-3 text-[11px] leading-relaxed text-dim"
             style={{ background: "var(--surface-2)" }} data-testid="terms-text">
          <p><b>Liability disclaimer.</b> Global Ore Exchange is a listing and communication platform only. We are not a party to any transaction, broker, escrow agent or guarantor.</p>
          <p>We do not verify the quality, quantity, legality, ownership or existence of any commodity listed, nor the identity or solvency of any member. All trades are conducted entirely at your own risk.</p>
          <p>Prices shown are for information only, may be delayed or indicative, and are not financial advice. We accept no liability for any loss, fraud, dispute or damages arising from use of the platform.</p>
          <p>You are responsible for due diligence, permits, export/import compliance and taxes in your jurisdiction.</p>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="glass flex items-center gap-3 rounded-2xl px-4 py-3.5">
      <Icon size={18} className="text-dim shrink-0" />
      <input {...props} data-testid={testid}
             className="w-full bg-transparent text-[15px] outline-none placeholder:text-dim"
             style={{ color: "var(--text)" }} />
    </div>
  );
}
